/**
 * @file Session guard helpers for maintenance and inactive-user enforcement.
 */

import { signOut } from "https://www.gstatic.com/firebasejs/12.19.0/firebase-auth.js";
import { auth } from "../firebase/firebase.js";
import {
    activeListeners,
    appData,
    setDataListenersAttachedStatus,
    state,
} from "../state/state.js";
import { detachDetailViewListeners } from "../data/detailListeners.js";

export const SESSION_STARTED_AT_KEY = "authSessionStartedAt";

let forcedMaintenanceSignOutInFlight = false;

/**
 * Stores a session start timestamp if one is not already present.
 * @returns {number} The session start time in milliseconds.
 */
export function ensureSessionStartedAt() {
    const existingValue = Number(sessionStorage.getItem(SESSION_STARTED_AT_KEY));
    if (Number.isFinite(existingValue) && existingValue > 0) {return existingValue;}

    const startedAt = Date.now();
    sessionStorage.setItem(SESSION_STARTED_AT_KEY, String(startedAt));
    return startedAt;
}

/**
 * Clears the session start timestamp.
 */
export function clearSessionStartedAt() {
    sessionStorage.removeItem(SESSION_STARTED_AT_KEY);
}

/**
 * Resets the in-flight forced sign-out guard.
 */
export function resetForcedMaintenanceSignOut() {
    forcedMaintenanceSignOutInFlight = false;
}

function getConfigTimeMs(value) {
    if (!value) {return null;}
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Forces sign-out for maintenance or inactive-account enforcement.
 * @param {string} message Message shown on the signed-out shell.
 * @param {object} [options] Guard callbacks.
 * @param {Function} [options.queueLoginNotice] Notice persistence callback.
 * @param {Function} [options.onFallback] Fallback renderer for sign-out errors.
 */
export function forceMaintenanceSignOut(message, options = {}) {
    if (forcedMaintenanceSignOutInFlight) {return;}
    forcedMaintenanceSignOutInFlight = true;
    options.queueLoginNotice?.(message);
    clearSessionStartedAt();

    activeListeners.forEach(unsubscribe => {
        try { unsubscribe(); } catch (e) { console.warn("Error unsubscribing listener:", e); }
    });
    activeListeners.length = 0;
    setDataListenersAttachedStatus(false);
    detachDetailViewListeners();

    signOut(auth).catch(error => {
        console.error("Forced maintenance sign out error:", error);
        options.onFallback?.();
    });
}

/**
 * Applies non-admin maintenance lock and logout-wave rules.
 * @param {object} [options] Guard callbacks.
 * @param {Function} [options.queueLoginNotice] Notice persistence callback.
 * @param {Function} [options.onFallback] Fallback renderer for sign-out errors.
 * @returns {boolean} Whether the user was forced out.
 */
export function enforceNonAdminMaintenanceState(options = {}) {
    const profile = state.currentUserProfile;
    const config = appData.config;
    if (!state.currentUser || !profile || !config || forcedMaintenanceSignOutInFlight) {return false;}
    if (profile.role === "admin") {return false;}

    const sessionStartedAt = ensureSessionStartedAt();
    const logoutWaveAt = getConfigTimeMs(config.nonAdminLogoutAt);

    if (config.nonAdminSignInLocked === true) {
        forceMaintenanceSignOut(
            "Non-admin sign-in is temporarily locked for maintenance.",
            options,
        );
        return true;
    }

    if (logoutWaveAt && sessionStartedAt <= logoutWaveAt) {
        forceMaintenanceSignOut(
            "You have been logged out due to system maintenance.",
            options,
        );
        return true;
    }

    return false;
}
